import {FormEvent, ReactNode, useId, useState} from "react";
import {Button, Container} from "react-bootstrap";
import {Link} from "react-router";
import {NewsSignupSource, OperatingSystem, ProductViewModel} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {accentProps} from "../../components/accent";
import {NotifyMeButton, NotifyMeModal} from "../../components/NotifyMeButton";
import {OperatingSystemLabel} from "../../components/OperatingSystemLabel";
import {htmlLead} from "../../components/sanitizeHtml";
import {allFormats, hasRequirements, platformIcons, sortedPlatforms} from "../../components/requirements";
import {sizeAttributes} from "../../components/imageSize";
import {DemoPlayer} from "../products/DemoPlayer";
import {checkoutPath, useSale} from "../products/useSale";
import {Reveal} from "./Reveal";
import {format, products} from "../../i18n";

const messages = products.showcase;

// The product's sections of a showcase page (layout/showcasePages.ts): the hero, Listen and the closing panel, each in
// the product's accent color (components/accent.ts).

// The operating systems the current version is for, in the order they first appear: for a product without system
// requirements (or none with platforms), which say instead.
const operatingSystems = (product: ProductViewModel): OperatingSystem[] =>
    [...new Set(product.currentVersion?.files.map(x => x.operatingSystem) ?? [])];

// The platforms and formats under the buttons, from the product's system requirements.
function RequirementsMeta({product}: { product: ProductViewModel }) {
    const requirements = product.requirements!;
    const formats = allFormats(requirements);
    return (
        <>
            <p className="showcase-meta">
                <span>{messages.runsOn}</span>
                {sortedPlatforms(requirements).map(x => (
                    <span key={x.platform} className="text-nowrap">
                        <i className={`bi ${platformIcons[x.platform]} me-1`} aria-hidden="true"></i>{products.requirements.platforms[x.platform]}
                    </span>
                ))}
            </p>
            {formats.length > 0 && (
                <p className="showcase-meta mt-1">
                    <span>{messages.formats}</span>
                    {formats.map(x => <span key={x} className="text-nowrap">{products.requirements.formatShortNames[x]}</span>)}
                </p>
            )}
        </>
    );
}

/**
 * The product's name (heading: h1 on its page, h2 on the home page), its description's first paragraph, the buttons
 * (actions) and the platforms (and formats, from its system requirements), over a glow of its color, then its first image, tilted back. top goes above the
 * name, note under the buttons. imageLink: where the image goes when clicked (the home page's: the product's page,
 * which visitors didn't always find from the Learn more button alone).
 */
export function ShowcaseHero({product, heading: Heading, actions, top, note, imageLink}: {
    product: ProductViewModel, heading: 'h1' | 'h2', actions: ReactNode, top?: ReactNode, note?: ReactNode, imageLink?: string
}) {
    const systems = operatingSystems(product);
    const image = product.images[0];
    const img = image && <img src={image.url} alt={image.altText} {...sizeAttributes(image)} fetchPriority="high"/>;
    return (
        <section className="showcase-hero" {...accentProps(product.accentColor)}>
            <Container className="showcase-hero-text">
                {top}
                {product.beta && <p className="showcase-pill">{products.closedBeta}</p>}
                <Heading className="showcase-display">{product.name}</Heading>
                <p className="showcase-lead">{htmlLead(product.description)}</p>
                <div className="showcase-actions">{actions}</div>
                {hasRequirements(product.requirements) && product.requirements.platforms.length > 0 ? (
                    <RequirementsMeta product={product}/>
                ) : systems.length > 0 && (
                    <p className="showcase-meta">
                        <span>{messages.runsOn}</span>
                        {systems.map(os => <OperatingSystemLabel key={os} os={os}/>)}
                    </p>
                )}
                {note}
            </Container>
            {/* The glow lights the image, never the text above it, which then always reads against the plain page. */}
            {image && (
                <div className="showcase-shot">
                    <div className="showcase-glow" aria-hidden="true"></div>
                    {/* Named for where it goes, not for the picture. */}
                    {imageLink
                        ? <Link to={imageLink} className="showcase-shot-link" aria-label={format(messages.imageLink, { product: product.name })}>{img}</Link>
                        : img}
                </div>
            )}
        </section>
    );
}

// The hero's usual buttons: Buy when it's on sale, otherwise the release sign-up while it's in Early Access; Listen when it has
// demos. extra: the page's own, after them.
export function ShowcaseActions({product, source, extra}: { product: ProductViewModel, source: NewsSignupSource, extra?: ReactNode }) {
    const {canBuy} = useSale(product);
    return (
        <>
            {canBuy && (
                <Button as={Link as any} to={checkoutPath(product)} variant="showcase" size="lg">
                    <i className="bi bi-cart me-2" aria-hidden="true"></i>{format(products.page.buy, { product: product.name })}
                </Button>
            )}
            {product.beta && !canBuy && <NotifyMeButton sku={product.sku} productName={product.name} source={source} variant="showcase" size="lg"/>}
            {product.demos.length > 0 && (
                <Button href="#listen" variant="showcase-outline" size="lg">
                    <i className="bi bi-play-fill me-1" aria-hidden="true"></i>{messages.listen}
                </Button>
            )}
            {extra}
        </>
    );
}

// The product's demos, in the player, in a panel of its color. Nothing without demos.
export function ShowcaseListen({product}: { product: ProductViewModel }) {
    const titleId = useId();
    if (product.demos.length === 0) {
        return null;
    }
    return (
        <section id="listen" className="showcase-section" {...accentProps(product.accentColor)} aria-labelledby={titleId}>
            <Container>
                <p className="showcase-eyebrow">{product.name}</p>
                <h2 id={titleId} className="showcase-title">{products.demos.title}</h2>
                <Reveal><div className="showcase-player"><DemoPlayer productId={product.id} demos={product.demos}/></div></Reveal>
            </Container>
        </section>
    );
}

// The end of the product's sections: while it's in Early Access, the release sign-up; on sale, Buy. Nothing otherwise.
export function ShowcaseClosing({product, source}: { product: ProductViewModel, source: NewsSignupSource }) {
    const {canBuy} = useSale(product);
    const titleId = useId();
    if (!product.beta && !canBuy) {
        return null;
    }
    return (
        <section className="showcase-section" {...accentProps(product.accentColor)} aria-labelledby={titleId}>
            <Container>
                <Reveal className="showcase-closing">
                    <div className="showcase-glow" aria-hidden="true"></div>
                    {product.beta ? (
                        <>
                            <h2 id={titleId} className="showcase-title mb-3">{format(messages.signup.title, { product: product.name })}</h2>
                            <p className="showcase-lead">{format(messages.signup.text, { product: product.name })}</p>
                            <ReleaseSignup product={product} source={source}/>
                        </>
                    ) : (
                        <>
                            <h2 id={titleId} className="showcase-title mb-3">{messages.own.title}</h2>
                            <p className="showcase-lead">{messages.own.text}</p>
                            <Button as={Link as any} to={checkoutPath(product)} variant="showcase" size="lg">
                                <i className="bi bi-cart me-2" aria-hidden="true"></i>{format(products.page.buy, { product: product.name })}
                            </Button>
                        </>
                    )}
                </Reveal>
            </Container>
        </section>
    );
}

// The release sign-up in the page: signed out, an email field whose button opens the sign-up (NotifyMeModal) with
// that address, for the bot check and the other choices; signed in, the usual button (the account's address).
function ReleaseSignup({product, source}: { product: ProductViewModel, source: NewsSignupSource }) {
    const {user} = useAuth();
    const [email, setEmail] = useState('');
    const [open, setOpen] = useState(false);
    const emailId = useId();

    if (user) {
        return <NotifyMeButton sku={product.sku} productName={product.name} source={source} variant="showcase" size="lg"/>;
    }

    const submit = (e: FormEvent) => {
        e.preventDefault();
        setOpen(true);
    };

    return (
        <>
            <form className="showcase-signup" onSubmit={submit}>
                <label htmlFor={emailId} className="visually-hidden">{messages.signup.email}</label>
                <input id={emailId} type="email" className="form-control" placeholder={messages.signup.email} autoComplete="email"
                       value={email} onChange={e => setEmail(e.target.value)}/>
                <Button type="submit" variant="showcase">{messages.signup.submit}</Button>
            </form>
            {open && <NotifyMeModal sku={product.sku} productName={product.name} source={source} initialEmail={email.trim()} onHide={() => setOpen(false)}/>}
        </>
    );
}
